import * as AppConstants from 'app/AppConstants';
import {loadById} from 'state/card/cardActions';
import {loadPageFiltersById} from 'state/stack/stackFilter/stackFilterActions';
import {
  selectAppliedPageFiltersByCardId,
  selectControlsActive,
} from 'state/stack/stackFilter/stackFilterSelectors';
import {loadViewById} from 'state/view/viewActions';
import {
  selectViewFiltersByCardId,
  selectViewById,
} from 'state/view/viewSelectors';
import {fetchFiltersFromQuerystring} from 'state/stack/stackFilter/stackFilterService';
import {loadDataSourcesByCardId} from 'state/dataSource/dataSourceActions';

// GENERAL NOTES ON HOW CARD FILTERS WORK:
// If you add a filter at the card details level, it is applied just to that card.
// When you apply a card filter it is only applied to the card but not persisted to any storage.
// A page refresh or page navigation will also clear any card filters.
// If you edit a filter at the card details level that came from the page, it will not change the page
// filter and is not persistent across a navigation or refresh.
// We only want to copy the page filters to the card filters if the page
// filter controls are open (see copyCardFiltersFromPageFilters below).

export const loadQuerystringCardFiltersById = (cardId) => (dispatch) => {
  dispatch({
    type: AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS_STARTED,
    payload: {cardId},
  });
  Promise.all([
    dispatch(loadDataSourcesByCardId(cardId)),
    dispatch(fetchFiltersFromQuerystring),
  ]).then(
    ([, filters]) => {
      dispatch({
        type: AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS,
        payload: {
          cardId,
          filters,
        },
      });
    },
    () => {
      dispatch({
        type: AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS_FAILED,
        payload: {cardId},
      });
    }
  );
};

export const addCardFilter = (cardId, dataSourceId, filterId) => ({
  type: AppConstants.CARD__ADDED_FILTER,
  payload: {
    cardId,
    filter: {
      id: filterId,
      dataSourceId,
      column: null,
      operand: null,
      values: [],
      applied: false,
    },
  },
});

export const applyCardFilter = (cardId, filter) => ({
  type: AppConstants.CARD__APPLIED_FILTER,
  payload: {
    cardId,
    filter,
  },
});

export const removeCardFilter = (cardId, filterId) => ({
  type: AppConstants.CARD__REMOVED_FILTER,
  payload: {
    cardId,
    filterId,
  },
});

export const copyCardFiltersFromPageFilters =
  (pageId, cardId) => (dispatch, getState) => {
    if (!selectControlsActive(getState(), pageId)) return;

    dispatch({
      type: AppConstants.CARD__COPY_FILTERS_FROM_PAGE_STARTED,
      payload: {
        cardId,
        pageId,
      },
    });

    dispatch(loadPageFiltersById(pageId)).then(() => {
      dispatch({
        type: AppConstants.CARD__COPY_FILTERS_FROM_PAGE_FINISHED,
        payload: {
          cardId,
          pageId,
          filters: selectAppliedPageFiltersByCardId(getState(), pageId, cardId),
        },
      });
    });
  };

export const clearCardFilters = (cardId) => ({
  type: AppConstants.CARD__FILTERS_CLEARED,
  payload: {
    cardId,
  },
});

export const applyCardFilterGroups = (cardId, filterGroupIds) => ({
  type: AppConstants.CARD__APPLIED_FILTER_GROUPS,
  payload: {
    cardId,
    filterGroupIds,
  },
});

export const loadCardStateFromView =
  (viewId, cardId) => (dispatch, getState) => {
    dispatch({
      type: AppConstants.CARD__LOAD_STATE_FROM_VIEW_STARTED,
      payload: {
        cardId,
        viewId,
      },
    });

    return Promise.all([
      dispatch(loadViewById(viewId)),
      dispatch(loadById(cardId)),
    ]).then(() => {
      const state = getState();
      const view = selectViewById(state, viewId);
      const filters = selectViewFiltersByCardId(getState(), viewId, cardId);

      dispatch({
        type: AppConstants.CARD__LOAD_STATE_FROM_VIEW_FINISHED,
        payload: {
          cardId,
          viewId,
          filters,
          segments: view.segmentIds,
          chartState: view.chartState,
          dateGrain: view.dateGrainInterval,
          dateRange: {
            dateRangeFilter: view.overrideDateRange
              ? view.dateRangeFilter
              : undefined,
          },
          overrideDateRange: view.overrideDateRange,
          functionOverrides: view.functionOverrides,
          filterGroupIds: view.filterGroupIds,
          dataControlFilters: view.dataControlFilters,
        },
      });
    });
  };

export const updateDateRange = (cardId, dateRange) => ({
  type: AppConstants.CARD__DATE_RANGE_UPDATED,
  payload: {
    cardId,
    dateRange,
  },
});

export const updateDateGrain = (cardId, dateGrain) => ({
  type: AppConstants.CARD__DATE_GRAIN_UPDATED,
  payload: {
    cardId,
    dateGrain,
  },
});

export const clearQueryStringError = (cardId) => ({
  type: AppConstants.CARD__CLEARED_QUERYSTRING_ERROR,
  payload: {cardId},
});
